import type { DocumentEmbeddableFile } from "@app/components/editor/document/types";
import {
  ActionFrame,
  CheckDone01,
  Code01,
  DoubleQuotes,
  Hash01,
  Heading01,
  Image01,
  List,
  ListSelect,
  Minus,
  Table,
  Type01,
} from "@dust-tt/sparkle";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import type { ChainedCommands } from "@tiptap/core";
import { isTextSelection } from "@tiptap/core";
import type { EditorState } from "@tiptap/pm/state";
import type { ComponentType } from "react";

/**
 * The `/` query typed alone at the start of a top-level paragraph, before the cursor. Menu entry
 * names are words, while a file search may hold any character but a line break.
 */
export const getBlockQuery = (
  state: EditorState,
  { fileSearch = false }: { fileSearch?: boolean } = {}
) => {
  const { selection } = state;
  if (!isTextSelection(selection) || !selection.empty) {
    return null;
  }
  const { $from } = selection;
  if ($from.depth !== 1 || $from.parent.type.name !== "paragraph") {
    return null;
  }
  const text = $from.parent.textBetween(0, $from.parentOffset);
  const match = (fileSearch ? /^\/([^\n]*)$/ : /^\/([\w ]*)$/).exec(text);
  return match ? { from: $from.start(), to: $from.pos, query: match[1] } : null;
};

interface DocumentBlock {
  name: MessageDescriptor;
  description: MessageDescriptor;
  icon: ComponentType<{ className?: string }>;
  keywords: string;
  apply: (chain: ChainedCommands) => boolean;
  selectionToolbarMenu?: "textStyle" | "list";
}

export const BLOCKS: DocumentBlock[] = [
  {
    name: msg({ message: "Text", context: "document paragraph style" }),
    description: msg`Start writing with plain text`,
    icon: Type01,
    keywords: "paragraph",
    selectionToolbarMenu: "textStyle",
    apply: (chain: ChainedCommands) => chain.setParagraph().run(),
  },
  {
    name: msg`Heading 1`,
    description: msg`A big section heading`,
    icon: Heading01,
    keywords: "h1 title",
    selectionToolbarMenu: "textStyle",
    apply: (chain: ChainedCommands) => chain.setHeading({ level: 1 }).run(),
  },
  {
    name: msg`Heading 2`,
    description: msg`A medium section heading`,
    icon: Heading01,
    keywords: "h2 subtitle",
    selectionToolbarMenu: "textStyle",
    apply: (chain: ChainedCommands) => chain.setHeading({ level: 2 }).run(),
  },
  {
    name: msg`Heading 3`,
    description: msg`A small section heading`,
    icon: Heading01,
    keywords: "h3 subtitle",
    selectionToolbarMenu: "textStyle",
    apply: (chain: ChainedCommands) => chain.setHeading({ level: 3 }).run(),
  },
  {
    name: msg`Bulleted list`,
    description: msg`A simple list of ideas`,
    icon: List,
    keywords: "bullet unordered",
    selectionToolbarMenu: "list",
    apply: (chain: ChainedCommands) => chain.toggleBulletList().run(),
  },
  {
    name: msg`Numbered list`,
    description: msg`Keep things in order`,
    icon: Hash01,
    keywords: "ordered",
    selectionToolbarMenu: "list",
    apply: (chain: ChainedCommands) => chain.toggleOrderedList().run(),
  },
  {
    name: msg`Task list`,
    description: msg`Track tasks with checkboxes`,
    icon: ListSelect,
    keywords: "todo checkbox checklist",
    selectionToolbarMenu: "list",
    apply: (chain: ChainedCommands) => chain.toggleTaskList().run(),
  },
  {
    name: msg`Numbered task list`,
    description: msg`Track tasks in order`,
    icon: CheckDone01,
    keywords: "todo checkbox checklist ordered steps",
    selectionToolbarMenu: "list",
    apply: (chain: ChainedCommands) => chain.toggleNumberedTaskList().run(),
  },
  {
    name: msg({ message: "Quote", context: "document paragraph style" }),
    description: msg`Make a passage stand out`,
    icon: DoubleQuotes,
    keywords: "blockquote",
    apply: (chain: ChainedCommands) => chain.toggleBlockquote().run(),
  },
  {
    name: msg({ message: "Code", context: "document paragraph style" }),
    description: msg`A code snippet`,
    icon: Code01,
    keywords: "codeblock",
    apply: (chain: ChainedCommands) => chain.toggleCodeBlock().run(),
  },
  {
    name: msg({ message: "Table", context: "document block" }),
    description: msg`Rows and columns, with a header row`,
    icon: Table,
    keywords: "grid rows columns",
    apply: (chain: ChainedCommands) =>
      chain.insertTable({ rows: 3, cols: 3 }).run(),
  },
  {
    name: msg`Divider`,
    description: msg`Separate sections`,
    icon: Minus,
    keywords: "line horizontal rule",
    apply: (chain: ChainedCommands) => chain.setHorizontalRule().run(),
  },
];

interface DocumentEmbed {
  kind: DocumentEmbeddableFile["kind"];
  name: MessageDescriptor;
  description: MessageDescriptor;
  pickerTitle: MessageDescriptor;
  icon: ComponentType<{ className?: string }>;
  keywords: string;
}

export const EMBEDS: DocumentEmbed[] = [
  {
    kind: "frame",
    name: msg({
      message: "Frame",
      context: "slash menu entry embedding a Frame",
    }),
    description: msg`Embed a live Frame from the files`,
    pickerTitle: msg`Choose a Frame`,
    icon: ActionFrame,
    keywords: "embed interactive dashboard chart",
  },
  {
    kind: "image",
    name: msg({
      message: "Image",
      context: "slash menu entry embedding an image",
    }),
    description: msg`Show an image from the files`,
    pickerTitle: msg`Choose an image`,
    icon: Image01,
    keywords: "picture photo embed",
  },
];
